import { Skeleton } from "antd"
import { AnimatePresence, motion } from "motion/react"
import React, { Suspense } from "react"

import { type RoleKeys } from "../../../../constants/roles.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import type { ProjectSectionKeys } from "./ProjectViewSectionsTabs.tsx"
import { FinancialAlertsPanel } from "./sections/ProjectViewFinance/FinancialAlertsPanel.tsx"
import { ProjectViewFinanceKpi } from "./sections/ProjectViewFinance/ProjectViewFinanceKpi.tsx"
import { ProjectViewInvoiceSnapshot } from "./sections/ProjectViewInvoices/ProjectViewInvoiceSnapshot.tsx"
import { ProjectViewIndicators } from "./sections/ProjectViewMilestones/ProjectViewIndicators.tsx"

const TABS_WITH_SNAPSHOT: ProjectSectionKeys[] = [
  "milestones",
  "finance",
  "invoices",
  "tasks",
  "commissions"
]

interface ProjectViewSidebarProps {
  activeTab: ProjectSectionKeys | null
  //
  showSalesSnapshot: boolean
  showCommissionSnapshot: boolean
  showInvoiceSnapshot: boolean
  showMilestoneSnapshot: boolean
}

export function ProjectViewSidebar({
  activeTab,
  showCommissionSnapshot,
  showSalesSnapshot,
  showInvoiceSnapshot,
  showMilestoneSnapshot
}: ProjectViewSidebarProps) {
  const collapsed = useAppSelector((state) => state?.project?.showSnapshot)

  const hasContent =
    activeTab !== null && TABS_WITH_SNAPSHOT.includes(activeTab)
  const isVisible = hasContent && !collapsed

  return (
    <motion.div
      initial={false}
      animate={{
        width: isVisible ? 565 : 0,
        opacity: isVisible ? 1 : 0
      }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className={`max-xl:hidden ${isVisible ? "ml-3" : ""}`}
    >
      <AnimatePresence mode="wait">
        {isVisible && (
          <motion.div
            key="snapshot"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="w-full flex overflow-auto tailwind-scroll flex-col gap-3 size-full"
          >
            <Suspense
              fallback={<Skeleton.Node active className="size-full flex-1" />}
            >
              {showMilestoneSnapshot && <ProjectViewIndicators />}
              {(showSalesSnapshot || showCommissionSnapshot) && (
                <>
                  <ProjectViewFinanceKpi
                    showSalesSnapshot={showSalesSnapshot}
                    showCommissionSnapshot={showCommissionSnapshot}
                  />
                  <FinancialAlertsPanel />
                </>
              )}
              {showInvoiceSnapshot && <ProjectViewInvoiceSnapshot />}
            </Suspense>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
